import { TableHead, TableRow, TableSortLabel } from "@mui/material";
import { get, startCase } from "lodash";
import { Dispatch, SetStateAction } from "react";
import { Order } from "../../../../typings/types";
import {
  Box,
  Grid2 as Grid,
  InputAdornment,
  TableCell,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

export function getComparator(order: string, orderBy: string) {
  return (a: string, b: string) => comparator(a, b, orderBy, order);
}

export function comparator<T>(a: T, b: T, orderBy: string, order: string): number {
  const aValue = get(a, orderBy);
  const bValue = get(b, orderBy);

  if (aValue === null || bValue === null) {
    return 0; // Handle null values (return 0 for equality)
  }

  // Determine the order multiplier (1 for asc, -1 for desc)
  const orderMultiplier = order === "asc" ? 1 : -1;

  if (typeof aValue === "string" && typeof bValue === "string") {
    return orderMultiplier * aValue.localeCompare(bValue); // String comparison
  }

  if (typeof aValue === "number" && typeof bValue === "number") {
    return orderMultiplier * (aValue - bValue); // Numeric comparison
  }

  // Handle Date comparison
  const aDate = new Date(aValue);
  const bDate = new Date(bValue);
  if (!isNaN(aDate.getTime()) && !isNaN(bDate.getTime())) {
    return orderMultiplier * (aDate.getTime() - bDate.getTime()); // Date comparison
  }

  return 0;
}

export function stableSort<T>(array: T[], comparator: (a: T, b: T) => number): T[] {
  const stabilizedThis = array?.map((el, index) => [el, index] as [T, number]);
  stabilizedThis?.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis?.map((el) => el[0]);
}

interface EnhancedTableHeadProps {
  order: Order;
  orderBy: string;
  onRequestSort: (event: React.MouseEvent<unknown>, property: string) => void;
  column: string[];
  disableSort: boolean;
}

export const EnhancedTableHead = ({
  order,
  orderBy,
  onRequestSort,
  column,
  disableSort,
}: EnhancedTableHeadProps) => {
  const createSortHandler = (property: string) => (e: React.MouseEvent<unknown>) => {
    onRequestSort(e, property);
  };

  return (
    <TableHead>
      <TableRow>
        {column?.map((headCell: any, key) => (
          <TableCell
            key={key}
            align={headCell.numeric ? "right" : "left"}
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === headCell.id ? order : false}
            sx={{
              right: 0,
              verticalAlign: "top",
              textWrap: "nowrap",
              minWidth: "40px",
              position: headCell.id === "-" ? "sticky" : "inherit",
            }}
          >
            <TableSortLabel
              disabled={disableSort}
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              <Typography variant="table_head">{headCell.label}</Typography>
              {orderBy === headCell.id ? (
                <Box component="span" sx={{ display: "none" }}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
};

interface TableToolbarProps {
  title: string;
  search: string;
  setSearch: Dispatch<SetStateAction<string>>;
  searchBy: string[];
  searchPlaceHolder: string;
}

// UAT 8/10 (T4a): this shared toolbar used to destructure only `{ title }`,
// silently dropping `search`/`setSearch`/`searchBy`/`searchPlaceHolder` even
// though every caller (Teacher/Parent DataTable) already passed them and
// already ran the client-side regex filter over `rows` — so typing in "the
// search box" did nothing because there never was a search box. Brought in
// line with the registrar-local copy of this component
// (RegistrarDataTable/helperTable.tsx — commit d0e11b3a, "close 7
// target-spec UI gaps"; PR #603 per the registrar Grade Books search), which
// already renders this exact search field. That file was deliberately
// forked off this one so the registrar's search box stayed isolated from
// parent/teacher/admin (see its commit message) — T4a supersedes that
// isolation for this shared copy specifically, since Parent and Teacher both
// route through it and Teacher's Students page needs the same fix. Parent
// tables that pass a non-empty `searchBy` will now also render a search box;
// see the T4a PR description for the explicit call-site audit.
export const TableToolbar = ({
  title,
  search,
  setSearch,
  searchBy,
  searchPlaceHolder,
}: TableToolbarProps) => {
  const showSearch = (searchBy?.length ?? 0) > 0;

  return (
    <Toolbar sx={{ px: { sm: 1 } }}>
      <Grid container width="100%" alignItems="center" justifyContent="space-between" gap={2}>
        <Grid>
          <Typography sx={{ flex: "1 1 100%" }} variant="header">
            {title}
          </Typography>
        </Grid>

        {showSearch && (
          <Grid>
            <TextField
              size="small"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={`Search ${searchPlaceHolder || ""}`.trim()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: { xs: "100%", sm: "240px" } }}
            />
          </Grid>
        )}
      </Grid>
    </Toolbar>
  );
};

const generateColumnHead = (fieldName: string, columnTitle?: string) => {
  let serializeLabel = columnTitle || startCase(fieldName);

  if (/Q \d/gi.test(serializeLabel)) {
    serializeLabel = serializeLabel.replace(/Q /, "Q");
  }

  return {
    id: fieldName,
    numeric: false,
    disablePadding: false,
    label: serializeLabel,
    // minWidth: fieldName.match(/title/gi) && "200px",
  };
};

export const customTableHeader = (headers: string[] = []) => {
  const columnHead: any[] = [];

  headers.map((x) => {
    switch (x) {
      // modify for abbreviation/renamed if need
      case "lesson_per_day":
        return columnHead.push(generateColumnHead(x, "No. of Lesson/day"));
      case "no_of_course":
        return columnHead.push(generateColumnHead(x, "No. of Course"));
      case "created_at":
        return columnHead.push(generateColumnHead(x, "Created on"));
      case "updated_at":
        return columnHead.push(generateColumnHead(x, "Last Modified on"));
      default:
        return columnHead.push(generateColumnHead(x));
    }
  });
  return columnHead;
};
